import { useState, useEffect, useRef, type FC } from 'react'
import type { InlineDropdownProps } from './types'
import ChevronDown from '@sylveraio/untitled-ui-icons-react/build/esm/ChevronDown'
import { InlineDropdownItem } from './InlineDropdownItem'
import clsx from 'clsx'

export const InlineDropdown: FC<InlineDropdownProps> = ({
  options,
  selectedOptionValue,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(
    null,
  )

  function handleOpenClick() {
    setIsOpen(!isOpen)
  }

  function handleSelect(value: string) {
    onSelect(value)
    setIsOpen(false)
    buttonRef.current?.focus() // Return focus to the button
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    event.preventDefault()

    switch (event.key) {
      case 'ArrowDown': {
        setFocusedOptionIndex((prevIndex) => {
          if (prevIndex === null) return 0
          return Math.min(prevIndex + 1, options.length - 1)
        })
        break
      }
      case 'ArrowUp': {
        setFocusedOptionIndex((prevIndex) => {
          if (prevIndex === null) return options.length - 1
          return Math.max(prevIndex - 1, 0)
        })
        break
      }
      case 'Enter': {
        if (!isOpen) {
          setIsOpen(true)
        } else if (focusedOptionIndex !== null) {
          handleSelect(options[focusedOptionIndex].value)
        }
        break
      }
      case 'Escape': {
        setIsOpen(false)
        buttonRef.current?.focus()
        break
      }
    }
  }

  const label = options.find(
    (option) => option.value === selectedOptionValue,
  )?.label

  useEffect(() => {
    if (focusedOptionIndex !== null && isOpen) {
      const activeOption = dropdownRef.current?.children[
        focusedOptionIndex
      ] as HTMLElement
      activeOption?.focus()
    }
  }, [focusedOptionIndex, isOpen])

  return (
    <div className="relative inline-block" onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        onClick={handleOpenClick}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={clsx(
          {
            'bg-medium hover:bg-medium': isOpen,
          },
          'rounded-lg px-1 text-content-action-default hover:bg-medium-hover',
        )}
      >
        <span>{label}</span>
        <ChevronDown className="inline h-6 w-6" />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          role="listbox"
          // Max height of 416px is 10 items
          className="absolute left-0 z-40 mt-2.5 max-h-[416px] overflow-scroll rounded-lg border border-default bg-high py-2 drop-shadow-md"
        >
          {options.map((option, index) => (
            <InlineDropdownItem
              id={`option-${index}`}
              selected={selectedOptionValue === option.value}
              label={option.label}
              key={option.value}
              onClick={() => handleSelect(option.value)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
